import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faMobile, // For StreetGains
  faBell, // For Streetfolio
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import Swal from "sweetalert2";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const TemplatePushNotificationList = () => {
  const navigate = useNavigate();
  const [temppushnotifications, setTemppushnotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const token = localStorage.getItem("authToken");
  const [senderList, setSenderList] = useState([]);

  // Search filter states
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [messageFilter, setMessageFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  useEffect(() => {
    fetchSenderList();
    fetchPushNotifications();
  }, []);

  const fetchSenderList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/emailconfig/dropdown`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const senderOptions = response.data?.data?.push?.map((item) => ({
        value: Number(item.value),
        label: item.label,
        sender: item.sender, // include sender here
      }));

      setSenderList(senderOptions || []);
    } catch (err) {
      console.error("Error fetching sender list", err);
    }
  };

  const fetchPushNotifications = async () => {
    try {
      setLoading(true);
      setIsRendering(true);
      const response = await axios.get(
        `${API_URL}settings/template/pushnotification/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setTemppushnotifications(response.data.data);
      setTotal(response.data.data.length);
      setPageCount(Math.ceil(response.data.data.length / limit));

      // Delay for skeleton effect
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    } catch (error) {
      console.error("Error fetching push notifications:", error);
      setIsRendering(false);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Added: Test Push Notification API integration
  const handleTestPushNotification = async (templateId, productApp) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}settings/template/pushnotification/test`,
        {},
        {
          params: {
            id: templateId,
            product_app: productApp,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      const appName = productApp === 0 ? "StreetGains" : "Streetfolio";
      Swal.fire({
        title: "Success!",
        text:
          response.data?.message ||
          `Test push notification sent to ${appName} successfully.`,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error sending Test Push Notification:", error);
      const appName = productApp === 0 ? "StreetGains" : "Streetfolio";
      Swal.fire({
        title: "Error!",
        text: `Failed to send test push notification to ${appName}. Please try again.`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  // ✅ End of added function

  const filteredTemplates = useMemo(() => {
    return temppushnotifications.filter((template) => {
      if (
        templateNameFilter &&
        !template.templatename
          .toLowerCase()
          .includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        titleFilter &&
        !template.title.toLowerCase().includes(titleFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        messageFilter &&
        !template.message.toLowerCase().includes(messageFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [temppushnotifications, templateNameFilter, titleFilter, messageFilter]);

  useEffect(() => {
    if (!temppushnotifications.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateNameFilter, titleFilter, messageFilter, temppushnotifications]);


  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit));
    setPage(1);
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice(start - 1, end);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_URL}settings/template/pushnotification/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        setTemppushnotifications((prev) =>
          prev.filter((rule) => rule.id !== id)
        );

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  const getSenderLabel = (senderType) => {
    const sender = senderList.find((s) => s.value === senderType);
    return sender
      ? `${sender.label} (${sender.sender || "-"})`
      : senderType || "-";
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setTitleFilter("");
      setMessageFilter("");
    };
    window.addEventListener('clearTemplatePushNotificationFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplatePushNotificationFilterEvent', handleClearFilters);
    };
  }, []);

  // Skeleton loader for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: 10 }).map((_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
            <div className="skeleton-text" style={{ width: "30px", height: "12px", margin: "0 auto" }}></div>
          </td>
          <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
            <div className="skeleton-text" style={{ width: "80%", height: "12px" }}></div>
          </td>
          <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
            <div className="skeleton-text" style={{ width: "70%", height: "12px" }}></div>
          </td>
          <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
            <div className="skeleton-text" style={{ width: "90%", height: "12px" }}></div>
          </td>
          <td>
            <div className="skeleton-text" style={{ width: "60%", height: "12px" }}></div>
          </td>
          <td className="text-center">
            <div className="d-flex gap-2 justify-content-center">
              <div className="skeleton-text" style={{ width: "30px", height: "15px", borderRadius: "4px" }}></div>
              <div className="skeleton-text" style={{ width: "30px", height: "15px", borderRadius: "4px" }}></div>
            </div>
          </td>
          <td style={{ width: "80px" }}>
            <div className="d-flex gap-2">
              <div className="skeleton-text" style={{ width: "30px", height: "30px", borderRadius: "4px" }}></div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      <div className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">
              Manage Template Push Notification
            </h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_pushnotification" action="add">
              <Link
                to="/Settings/TemplatePushNotification/add"
                className="btn btn-default btn-sm me-2"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Push Notification Template
              </Link>
            </PermissionWrapper>

            {!loading && temppushnotifications.length > 0 && (
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="table-responsive sticky-table-container">
            <style>
              {`
                /* Sticky header rows */
                .sticky-push-table thead tr:first-child th {
                   position: sticky;
                   top: 0;
                   z-index: 20;
                   background: #f8f9fa;
                 }

                .sticky-push-table thead tr:nth-child(2) th {
                  position: sticky;
                  top: 30px; /* Adjust based on first header row height */
                  z-index: 19;
                  background: white;
                 }

                .sticky-table-container {
                   max-height: calc(100vh - 152px);
                  overflow: auto;
                }

                 .sticky-push-table th {
                   vertical-align: top;
                  }

                /* Skeleton Loading Styles */
                .skeleton-text {
                  background: linear-gradient(
                    90deg,
                    #f0f0f0 25%,
                    #e0e0e0 50%,
                    #f0f0f0 75%
                  );
                  background-size: 200% 100%;
                  animation: skeleton-loading 1.5s ease-in-out infinite;
                  border-radius: 4px;
                  display: inline-block;
                }

                .skeleton-row td {
                  padding: 12px 8px !important;
                  vertical-align: middle !important;
                }

                @keyframes skeleton-loading {
                  0% {
                    background-position: 200% 0;
                  }
                  100% {
                    background-position: -200% 0;
                  }
                }
              `}
            </style>
            <table className="table m-b-0 sticky-push-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>#</th>
                  <th>Template Name</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Sender Type</th>
                  <th style={{ width: "120px", textAlign: "center" }}>
                    Test Push Notification {/* ✅ Added header */}
                  </th>
                  <th style={{ width: "80px" }}>Action</th>
                </tr>
                <tr>
                  <th style={{ textAlign: "center" }}></th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search name..."
                        value={templateNameFilter}
                        onChange={(e) =>
                          setTemplateNameFilter(e.target.value)
                        }
                        disabled={loading || isRendering}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search title..."
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                        disabled={loading || isRendering}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search message..."
                        value={messageFilter}
                        onChange={(e) => setMessageFilter(e.target.value)}
                        disabled={loading || isRendering}
                      />
                    </div>
                  </th>
                  <th></th>
                  <th></th>
                  <th>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => {
                        setTemplateNameFilter("");
                        setTitleFilter("");
                        setMessageFilter("");
                      }}
                      title="Clear filters"
                      disabled={loading || isRendering}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || isRendering || filterLoading ? (
                  <TableSkeleton />
                ) : paginatedTemplates.length > 0 ? (
                  paginatedTemplates.map((post, index) => (
                    <tr key={post.id}>
                      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                        {post.templatename}
                      </td>
                      <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                        {post.title}
                      </td>
                      <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                        {post.message}
                      </td>
                      <td>{getSenderLabel(post.sender_type)}</td>

                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            onClick={() => handleTestPushNotification(post.id, 0)}
                            className="btn btn-default btn-xs"
                            title="Send Test to StreetGains"
                          >
                            <FontAwesomeIcon icon={faMobile} />
                          </button>
                          <button
                            onClick={() => handleTestPushNotification(post.id, 1)}
                            className="btn btn-default btn-xs"
                            title="Send Test to Streetfolio"
                          >
                            <FontAwesomeIcon icon={faBell} />
                          </button>
                        </div>
                      </td>

                      <td style={{ width: "80px" }}>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="settings_pushnotification"
                            action="edit"
                          >
                            <Link
                              to={`/Settings/TemplatePushNotification/edit/${post.id}`}
                              className="btn btn-warning btn-xs"
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Link>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      {temppushnotifications.length === 0
                        ? "No records found. Add Push Notification Template to get started."
                        : "No templates match your search criteria."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default TemplatePushNotificationList;